import { Controller } from '@hotwired/stimulus';
import { openModal } from './modal_controller';

// The download card (job_trackings/_download_progress) in the job progress modal. It follows the
// download-decrypt-convert:progress event on window, so the buttons in the views need no outlet
// attribute. The partial shows the pill and body matching data-state.
// One shared card: two downloads at the same time overwrite each other's card.
//
// event.detail: { state, ratio, bytes, total, fileName, rename }. state is downloading, decrypting,
// building_xlsx, done or failed; ratio is 0..1, or null for an unknown total (pulsing full bar,
// no percent); a fileName starts a download, or renames it with rename: true (which doesn't
// reopen a modal closed in the meantime).
const THROTTLE_MS = 250;

export default class extends Controller {
  static targets = ['filename', 'bar', 'fill', 'label', 'percent'];

  last = { state: '', at: 0 };

  update({ detail }) {
    // stay idle after the modal closed mid-download, and repaint at most every 250 ms (progress
    // arrives per network chunk); a file name or a new state always gets through the throttle
    const starting = detail.fileName && !detail.rename;
    const idle = this.element.dataset.state === 'idle';
    const throttled = detail.state === this.last.state && Date.now() - this.last.at < THROTTLE_MS;
    if ((idle && !starting) || (throttled && !detail.fileName)) return;

    if (starting) this.open();
    this.render(detail);
  }

  // Opens the modal unless it's already open (the sampled data / verification flows download
  // from inside it). Closing it resets the card, even mid-download; done or failed stay until then.
  open() {
    const modal = this.element.closest('[data-controller~="modal"]');
    const dialog = modal.querySelector('dialog');
    if (!dialog.open) openModal(modal.id);
    dialog.onclose = () => (this.element.dataset.state = 'idle'); // onclose: no stacked handlers
  }

  render({ state, ratio = null, bytes = 0, total = 0, fileName }) {
    this.last = { state, at: Date.now() };
    this.element.dataset.state = state;
    if (fileName) this.filenameTarget.textContent = fileName;

    const percent = ratio === null ? null : Math.round(ratio * 100);
    this.fillTarget.style.width = `${percent ?? 100}%`;
    this.fillTarget.classList.toggle('animate-pulse', percent === null);
    if (percent === null)
      this.barTarget.removeAttribute('aria-valuenow'); // indeterminate
    else this.barTarget.setAttribute('aria-valuenow', String(percent));

    const mb = (n) => (n / (1024 * 1024)).toFixed(1);
    this.labelTarget.textContent = bytes ? `${mb(bytes)}${total ? ` / ${mb(total)}` : ''} MB` : '';
    this.percentTarget.textContent = percent === null ? '' : `${percent}%`;
  }
}
